'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Box, Button } from '@mui/material';
import RootLayout from '../../layout';

interface LoginProps {
  onSuccess?: () => void;
}

interface LoginResponse {
  access_token?: string;
  detail?: string;
  message?: string;
  [key: string]: unknown;
}

export default function Login({ onSuccess }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  // TODO check how to do that with next navigation
  /*useEffect(() => {
    if (router.query.expired) {
      setError('Your session is expired! Please log in again.');
    }
  }, [router.query]);
*/
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_AUTH_URL}/auth/jwt/login`,
        {
          method: 'POST',
          credentials: 'include',
          headers: new Headers({
            'Content-Type': 'application/x-www-form-urlencoded',
          }),
          body: new URLSearchParams({
            username: email, // FastAPI Users expects "username" = email
            password: password,
          }),
        },
      );
      const raw = await res.text();
      let data: LoginResponse = {};
      try {
        data = raw ? (JSON.parse(raw) as LoginResponse) : {};
      } catch {
        console.log('inside catch');
        data = { detail: raw || '' };
      }
      if (!res.ok) {
        if (res.status === 401 || res.status === 400) {
          setError(data.detail || 'Incorrect email or password');
        } else if (res.status === 422) {
          setError('Validation error: please check your input.');
        } else {
          setError(
            (data && (data.detail || data.message)) ||
              `Login failed (status ${res.status})`,
          );
        }
        return;
      }
      if (!data || !data.access_token) {
        setError('Login succeeded but server did not return a token.');
        return;
      }
      localStorage.setItem('access_token', data.access_token);
      document.cookie = `access_token=${data.access_token}; path=/; max-age=3600`;
      onSuccess?.();
      router.push('/');
      //router.push('/home');
    } catch (err) {
      setError('Network error — please check the server and try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RootLayout>
      <Box
        component='main'
        sx={{
          width: '100%',
          px: '5px',
          pb: '5px',
          pt: '85px',
          maxHeight: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '500px',
              minWidth: '330px',
              padding: '40px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              textAlign: 'center',
            }}
          >
            <img
              src='/static/images/logo-argos.png'
              alt='ARGOS logo'
              style={{ maxWidth: '200px', marginBottom: '50px' }}
            />

            {error && (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '10px',
                  backgroundColor: '#f8d7da',
                  color: '#842029',
                  borderRadius: '4px',
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <label
                  htmlFor='email'
                  style={{ display: 'block', marginBottom: '5px' }}
                >
                  Email address
                </label>
                <input
                  id='email'
                  type='email'
                  placeholder='Email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <label
                  htmlFor='password'
                  style={{ display: 'block', marginBottom: '5px' }}
                >
                  Password
                </label>
                <input
                  id='password'
                  type='password'
                  placeholder='Password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <input
                  type='checkbox'
                  id='remember'
                  defaultChecked
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor='remember'>Remember me</label>
              </div>
              <Button
                type='submit'
                disabled={loading}
                color='primary'
                variant='contained'
                sx={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: 'none',
                  //backgroundColor: theme.palette.secondary.main,
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Logging in...' : 'Sign in'}
              </Button>
            </form>
          </div>
        </Box>
      </Box>
    </RootLayout>
  );
}
